import { ChevronDownIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  forwardRef,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useToast,
} from '@chakra-ui/react'
import type { Asset } from '@shapeshiftoss/asset-service'
import type { ChainId } from '@shapeshiftoss/caip'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FaInfoCircle } from 'react-icons/fa'
import { useTranslate } from 'react-polyglot'
import { useSelector } from 'react-redux'
import { AssetIcon } from 'components/AssetIcon'
import { RawText } from 'components/Text'
import { getChainAdapterManager } from 'context/PluginProvider/chainAdapterSingleton'
import { useModal } from 'hooks/useModal/useModal'
import { useWallet } from 'hooks/useWallet/useWallet'
import { deriveAccountIdsAndMetadata } from 'lib/account/account'
import { isSome } from 'lib/utils'
import { portfolio, portfolioApi } from 'state/slices/portfolioSlice/portfolioSlice'
import {
  selectAssets,
  selectMaybeNextAccountNumberByChainId,
  selectPortfolioChainIdsSortedFiat,
} from 'state/slices/selectors'
import { useAppDispatch, useAppSelector } from 'state/store'

type ChainOptionProps = {
  chainId: ChainId
  asset: Asset
  setSelectedChainId: (chainId: ChainId) => void
}
const ChainOption = forwardRef<ChainOptionProps, 'button'>(
  ({ chainId, asset, setSelectedChainId }, ref) => (
    <MenuItemOption
      ref={ref}
      key={chainId}
      iconSpacing={0}
      onClick={() => setSelectedChainId(chainId)}
    >
      <Stack direction='row' spacing={0} ml={0}>
        <AssetIcon size='xs' asset={asset} mr={3} />
        <RawText fontWeight='bold'>{asset?.networkName ?? asset.name}</RawText>
      </Stack>
    </MenuItemOption>
  ),
)

export const AddAccountModal = () => {
  const translate = useTranslate()
  const toast = useToast()
  const dispatch = useAppDispatch()

  const {
    state: { wallet },
  } = useWallet()

  const assets = useSelector(selectAssets)
  const chainIds = useSelector(selectPortfolioChainIdsSortedFiat)

  const firstChainId = useMemo(() => chainIds[0], [chainIds])
  const [selectedChainId, setSelectedChainId] = useState<ChainId>(firstChainId)

  const filter = useMemo(() => ({ chainId: selectedChainId }), [selectedChainId])
  const [isAbleToAddAccount, nextAccountNumber] = useAppSelector(s =>
    selectMaybeNextAccountNumberByChainId(s, filter),
  )

  const { addAccount } = useModal()
  const { close, isOpen } = addAccount

  useEffect(() => {
    setSelectedChainId(chainIds[0])
  }, [chainIds])

  const menuOptions = useMemo(() => {
    const chainAdapterManager = getChainAdapterManager()
    return chainIds
      .map(chainId => {
        const chainAdapter = chainAdapterManager.get(chainId)
        if (!chainAdapter) return null
        const assetId = chainAdapter.getFeeAssetId()
        const asset = assets?.[assetId]
        if (!asset) return null
        const key = chainId
        const chainOptionsProps = { chainId, setSelectedChainId, asset, key }
        return <ChainOption {...chainOptionsProps} />
      })
      .filter(isSome)
  }, [assets, chainIds])

  const asset = useMemo(() => {
    if (!selectedChainId) return
    return assets?.[getChainAdapterManager().get(selectedChainId)!.getFeeAssetId()]
  }, [assets, selectedChainId])

  const handleAddAccount = useCallback(() => {
    if (!wallet) return
    if (!selectedChainId) return
    if (!nextAccountNumber) return
    ;(async () => {
      const accountNumber = nextAccountNumber
      const chainIds = [selectedChainId]
      const accountMetadataByAccountId = await deriveAccountIdsAndMetadata({
        accountNumber,
        chainIds,
        wallet,
      })

      const { getAccount } = portfolioApi.endpoints
      const opts = { forceRefetch: true }
      dispatch(portfolio.actions.upsertAccountMetadata(accountMetadataByAccountId))
      const accountIds = Object.keys(accountMetadataByAccountId)
      accountIds.forEach(accountId =>
        dispatch(getAccount.initiate({ accountId, upsertOnFetch: true }, opts)),
      )
      const assetId = getChainAdapterManager().get(selectedChainId)!.getFeeAssetId()
      const { name } = assets[assetId] ?? {}
      toast({
        position: 'top-right',
        title: translate('accounts.newAccountAdded', { name }),
        description: translate('accounts.youCanNowUse', { name, accountNumber }),
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      close()
    })()
  }, [assets, close, dispatch, nextAccountNumber, selectedChainId, toast, translate, wallet])

  if (!asset) return null

  return (
    <Modal isOpen={isOpen} onClose={close} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign='center'>{translate('accounts.addAccount')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody alignItems='center' justifyContent='center'>
          <Stack>
            <Stack spacing={0}>
              <RawText fontWeight='semibold'>{translate('accounts.accountChain')}</RawText>
              <RawText mt={-4} fontSize='sm' color='gray.500'>
                {translate('accounts.selectChain')}
              </RawText>
            </Stack>
            <Box pt={4} width='full'>
              <Menu matchWidth>
                <MenuButton
                  mb={4}
                  as={Button}
                  width='full'
                  variant='outline'
                  iconSpacing={0}
                  rightIcon={<ChevronDownIcon />}
                >
                  <Stack spacing={0} direction='row' alignItems='center'>
                    <AssetIcon size='xs' asset={asset} mr={3} />
                    <RawText fontWeight='bold'>{asset?.networkName ?? asset.name}</RawText>
                  </Stack>
                </MenuButton>
                <MenuList>
                  <MenuOptionGroup defaultValue='asc' type='radio'>
                    {menuOptions}
                  </MenuOptionGroup>
                </MenuList>
              </Menu>
            </Box>
            {!isAbleToAddAccount && (
              <Alert size='sm'>
                <AlertIcon as={FaInfoCircle} />
                <AlertDescription>{translate('accounts.requiresPriorTxHistory')}</AlertDescription>
              </Alert>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme='blue'
            width='full'
            disabled={!isAbleToAddAccount}
            onClick={handleAddAccount}
          >
            {translate('accounts.addAccount')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
